// Card #3311 -- one place that downloads and DECODES scoreboard pictures
// before anything shows them, and remembers which ones are ready.
//
// Why. A big-screen board must never show a picture painting top to bottom in
// strips while it downloads. So a picture is requested into an off-screen
// `Image()` first and only counts as "ready" once `decode()` has resolved
// (fully downloaded AND decoded). ImageLayout keeps its <img> invisible until
// then, and the board page keeps the previous slide on screen until the next
// slide's picture is ready (useHeldSlideshowData). The next slide is requested
// during the current slide's dwell (the payload names it in
// `slideshow.nextImageAssetUrl`), so on its turn it is already in the browser's
// memory cache.
//
// Identity. Entries are keyed by the S3 key (the URL path, query string
// ignored), not by the presigned URL: the signature differs between polls that
// land on different Lambda instances (#2321), but the picture is the same.
// An entry PINS the URL it was first loaded from, and ImageLayout uses that
// exact string as `src`, so the browser cache hits for a slide shown a second
// time in the loop and across polls instead of downloading it again.
//
// A failed or hung load is recorded as `failed`. It is the consumer's job to
// fall back (ImageLayout renders Blank + logo): nothing here ever throws or
// shows an error. A failed entry is retried at most every FAILED_RETRY_MS
// unless the caller forces it (ImageLayout's own backoff does).
import { useSyncExternalStore } from "react";

export const IMAGE_LOAD_TIMEOUT_MS = 30 * 1000;
export const FAILED_RETRY_MS = 30 * 1000;
export const MAX_SLIDE_IMAGE_ENTRIES = 64;
// A pinned URL within this long of its signed expiry is replaced by a newer one.
export const EXPIRY_MARGIN_MS = 10 * 60 * 1000;

export const IMAGE_STATUS = { LOADING: "loading", READY: "ready", FAILED: "failed" };

export function imageKeyOf(url) {
  if (typeof url !== "string" || !url) return null;
  const queryIndex = url.indexOf("?");
  return queryIndex === -1 ? url : url.slice(0, queryIndex);
}

const entries = new Map(); // key -> { key, url, status, failedAt }
const listeners = new Set();
let version = 0;

function emit() {
  version += 1;
  listeners.forEach((listener) => listener());
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Resolves once the picture is fully downloaded and decoded; rejects on a
// network or bad-bytes failure. It has NO timeout of its own and never aborts
// the download: the callers decide what "taking too long" means (see start()).
function loadAndDecode(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = "async";
    img.onerror = () => reject(new Error("image failed to load"));
    img.src = url;
    if (typeof img.decode === "function") {
      img.decode().then(() => resolve(img), reject);
    } else {
      // No decode() (very old browsers): the load event is the best signal.
      img.onload = () => resolve(img);
    }
  });
}

// Presigned URLs carry X-Amz-Date (YYYYMMDDTHHMMSSZ) and X-Amz-Expires
// (seconds). Returns the expiry as epoch ms, or null for a URL without them
// (never treated as expiring).
export function urlExpiryMs(url) {
  if (typeof url !== "string") return null;
  const queryIndex = url.indexOf("?");
  if (queryIndex === -1) return null;
  const params = new URLSearchParams(url.slice(queryIndex + 1));
  const date = params.get("X-Amz-Date");
  const expires = Number(params.get("X-Amz-Expires"));
  const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(date || "");
  if (!match || !Number.isFinite(expires) || expires <= 0) return null;
  const [, y, mo, d, h, mi, sec] = match;
  return Date.UTC(+y, +mo - 1, +d, +h, +mi, +sec) + expires * 1000;
}

function start(key, url) {
  const entry = { key, url, expiresAt: urlExpiryMs(url), status: IMAGE_STATUS.LOADING, failedAt: 0, upgradingTo: null };
  entries.delete(key);
  entries.set(key, entry);
  while (entries.size > MAX_SLIDE_IMAGE_ENTRIES) entries.delete(entries.keys().next().value);
  // A hung load releases whatever is waiting on it (the board falls back to
  // Blank + logo) but does NOT abort the download: if it finishes later the
  // entry flips to READY and the picture shows.
  const timer = setTimeout(() => {
    if (entries.get(key) !== entry || entry.status !== IMAGE_STATUS.LOADING) return;
    entry.status = IMAGE_STATUS.FAILED;
    entry.failedAt = Date.now();
    emit();
  }, IMAGE_LOAD_TIMEOUT_MS);
  loadAndDecode(url).then(
    () => {
      clearTimeout(timer);
      // A newer attempt for this key replaced this one: its result is stale.
      if (entries.get(key) !== entry) return;
      entry.status = IMAGE_STATUS.READY;
      emit();
    },
    () => {
      clearTimeout(timer);
      if (entries.get(key) !== entry || entry.status === IMAGE_STATUS.FAILED) return;
      entry.status = IMAGE_STATUS.FAILED;
      entry.failedAt = Date.now();
      emit();
    }
  );
  emit();
  return entry;
}

// A pinned URL that is expired, or about to be, is replaced by a newer polled
// URL for the same picture. The old entry stays READY (the picture stays on
// screen, no flash) until the new URL is downloaded and decoded; only then does
// entry.url swap.
function upgradePinnedUrl(entry, url) {
  if (entry.status !== IMAGE_STATUS.READY || entry.upgradingTo || url === entry.url) return;
  if (entry.expiresAt === null || Date.now() < entry.expiresAt - EXPIRY_MARGIN_MS) return;
  entry.upgradingTo = url;
  const giveUp = setTimeout(() => {
    if (entry.upgradingTo === url) entry.upgradingTo = null;
  }, IMAGE_LOAD_TIMEOUT_MS);
  loadAndDecode(url).then(
    () => {
      clearTimeout(giveUp);
      if (entries.get(entry.key) !== entry) return;
      entry.url = url;
      entry.expiresAt = urlExpiryMs(url);
      entry.upgradingTo = null;
      emit();
    },
    () => {
      clearTimeout(giveUp);
      entry.upgradingTo = null;
    }
  );
}

// Start (or reuse) the download for `url`'s picture. `force` restarts it with
// `url` whatever the current state is (a consumer's retry). Returns the entry.
export function preloadSlideImage(url, { force = false } = {}) {
  const key = imageKeyOf(url);
  if (!key) return null;
  const existing = entries.get(key);
  if (existing && !force) {
    // True LRU: a hit moves the entry to the newest end of the Map.
    entries.delete(key);
    entries.set(key, existing);
    if (existing.status !== IMAGE_STATUS.FAILED) {
      upgradePinnedUrl(existing, url);
      return existing;
    }
    if (Date.now() - existing.failedAt < FAILED_RETRY_MS) return existing;
  }
  return start(key, url);
}

export function getSlideImageEntry(url) {
  const key = imageKeyOf(url);
  return key ? entries.get(key) ?? null : null;
}

// Re-renders the caller whenever any entry changes state.
export function useSlideImageVersion() {
  return useSyncExternalStore(subscribe, () => version);
}

export function __resetSlideImageCacheForTests() {
  entries.clear();
  version += 1;
}
